import React, { useEffect, useState } from 'react';
import Modal, { FullScreen } from '../../../../global/components/modal';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { Row, Column } from '../../../../global/components/row';
import { size, get, set, first, sortBy, upperFirst } from '../../../../global/lib/lodash';
import { Button, IconButton } from '../../../../global/components/Button';
import { Text } from '../../../../global/components/text';
import { useStyles } from './style';
import { RoomSelect } from '../../../../global/components/room-select';
import { CenterLoader, Loader } from '../../../../global/styles';
import { Icon } from '@mui/material';
import colors from '../../../../global/styles/colors';
import SnackAlerts from '../../../../global/components/snack-alerts';
import sendData from '../../../../redux/actions/sendData';
import { useWindowSize } from '../../../../global/hooks/useWindowSize';
import { useIntersect } from '../../../../global/hooks/useIntersect';
import Confirm from '../../../../global/components/confirm';
import { EDITIONINCLUDES } from '../manageedition';
import { useUser } from '../../../../global/hooks/useUser';

const Doc = props => {
  const { updateChecks, copiedRooms, onCopy, isMobileXS, rooms, doc } = props;

  const styles = useStyles();

  const [ref, view] = useIntersect({ rootMargin: '0px' });

  const fixMargins = { marginLeft: 5, pointerEvents: 'none' };

  const title = (style = { minWidth: 200 }) => {
    return (
      <Row style={style}>
        <Text type="title_medium" bold>{`${get(doc, 'document.classification.system.name', get(doc, 'classification.name', ''))} (${size(doc.items)}):`}</Text>
      </Row>
    )
  };

  const CONT = isMobileXS ? Column : Row;

  const renderCP = (item, _i) => {
    const cpTitle = (style = { cursor: 'pointer', minWidth: 200, maxWidth: isMobileXS ? undefined : '50%' }) => {
      return (
        <Row style={style}>
          <Text type="label_large">{`${get(item, 'document.name')} (${upperFirst(get(item, 'documentableType', ''))})`}</Text>
        </Row>
      )
    };

    return (
      <div key={_i} className={styles.rowSpace}>
        <CONT>
          {cpTitle()}
          <div style={{ marginLeft: 'auto' }}>
            <RoomSelect type="inlineModal" onCopy={onCopy} onPaste={copiedRooms ? () => copiedRooms : undefined} value={item.rooms} compare={item.rooms} rooms={rooms} onChange={(s, areEqual) => updateChecks(doc, s, item, areEqual)} modalTitle={cpTitle(fixMargins)} highlightOriginals />
          </div>
        </CONT>
      </div>
    )
  }

  return (
    <div style={{ marginTop: 10 }} ref={ref}>
      <CONT className={styles.checklistHeader}>
        {title()}
        <div style={{ marginLeft: 'auto' }}>
          <RoomSelect rooms={rooms} onCopy={onCopy} onPaste={copiedRooms ? () => copiedRooms : undefined} onChange={(s, areEqual) => updateChecks(doc, s, null, areEqual)} type="inlineModal" modalTitle={title({ ...fixMargins, marginLeft: 30, marginTop: 4 })} />
        </div>
      </CONT>

      <div className={styles.marginLeft}>
        {view.isIntersecting && doc.items.map(renderCP)}
      </div>
    </div>
  )
}

export const RealmAssignments = props => {
  const { editionId } = props;
  const [realms, setRealms] = useState([]);
  const [state, setState] = useState({
    copiedRooms: null,
    loading: false,
    edDocs: []
  })
  const [alerts, setAlerts] = useState();
  const [preview, setPreview] = useState({
    open: false,
    onClose: () => {
      setPreview(p => ({ ...p, open: false }))
    }
  })
  const [docs, setDocs] = useState([]);

  const styles = useStyles();
  const { userRealm } = useUser();
  
  const { isMobileXS } = useWindowSize();

  const edition = useRetrieveData({ url: `/editions/${editionId}` }, {
    build: ['editions'],
    params: {
      include: EDITIONINCLUDES.join(','),
    },
    transformData: t => {
      return t.find(tt => tt.id == editionId)
    },
  }) 

  const edDocs = useRetrieveData(
    { url: `/edition-documents` },
    {
      build: ['editionDocuments'],
      params: {
        filter: {
          edition_id: editionId,
        },
        include: 'classification,document,superset,classification.system',
      },
    }
  );

  const roomData = useRetrieveData({ url: '/realms' }, {
    build: ['realms'],
    params: {
      filter: {
        tier: 'division',
        parent_id: userRealm.id,
      },
      include: 'parent'
    }
  })

  useEffect(() => {
    if (!edDocs.loading && !edition.loading) {
      let edocs;

      const classes = {};

      edDocs.data.map((ed) => {
        const classificationId = get(ed, 'classification.system.id');

        set(ed, 'document.classification', ed.classification);

        ed.rooms = get(edition, `data.config.docs.${ed.id}.realms`);

        set(classes, classificationId, sortBy([...get(classes, classificationId, []), ed], 'order'));
      });

      edocs = sortBy(
        Object.keys(classes).map((cl) => ({ ...first(classes[cl]), items: classes[cl] })),
        'order'
      );

      setDocs(edocs);
    }
  }, [edDocs.loading, edition.loading]);

  useEffect(() => {
    if (!roomData.loading) {
      const rms = roomData.data.map(r => ({ ...r, groupBy: get(r, 'parent.name') }))
      setRealms(rms);
    }
  }, [roomData.loading])

  const onChange = () => {
    props.onChange && props.onChange();
  }

  const updateChecks = (d, sel, item, areEqual) => {
    if (!item) {
      d.rooms = sel;
    }
    setDocs(clss => {
      return clss.map(doc => {
        if (doc.id == d.id) {
          doc = {
            ...doc,
            items: doc.items.map(c => {
              const setRooms = () => {
                c.rooms = sel;
              }
  
              if (item) {
                if (c.id == item.id) {
                  setRooms();
                }
              } else {
                setRooms();
              }
              
              return c;
            })
          }
        }
        return doc;
      })
    })

    if (!areEqual) {
      onChange();
    }
  }

  const onCopy = (rms) => {
    setState(s => ({ ...s, copiedRooms: rms }));
    setAlerts({ 
      message: () => <div>Rooms copied to clipboard!<br />Use <Icon>post_add_oulined</Icon> to paste to another category</div> 
    })
  }

  const cancel = () => {
    props.onCancel && props.onCancel();
  }

  const previewClick = realm => {
    let hasone = false;
    let content = (
      <div>
        {Object.keys(docs).map(k => {
          const doc = docs[k];
          const reldocs = doc.items.filter(item => !size(item.rooms) ? true : item.rooms.find(room => room.id == realm.id));
          if (!size(reldocs)) { return null }
          hasone = true;

          return (
            <div>
              <Text type="title_medium" bold>{`${get(doc, 'items.0.classification.system.name', '')} (${size(reldocs)}):`}</Text>
              <div>
                {reldocs.map(item => {
                  return <Text type="title_medium">{`${get(item, 'document.name', '')}`}</Text>
                })}
              </div>
            </div>
          )
        })}
      </div>
    )

    if (!hasone) {
      content = <div><Text type="title_medium" bold>{`None`}</Text></div>
    }
    setPreview(p => ({
      ...p,
      realm,
      open: true,
      title: `${realm.name} Criteria Preview`,
      content,
    }))
  }

  const renderRealmsList = () => {
    return (
      <div className={styles.marginLeft}>
        {edition.loading && <Loader />}
        {realms.map(realm => {
          let count = 0;
          Object.keys(docs).map((k) => {
            const doc = docs[k];
            doc.items.find(item => {
              if (!size(item.rooms) || item.rooms.find(room => room.id == realm.id)) {
                count += 1
              }
            })
          })
          return (
            <Row className={styles.rowSpace}>
              <Text type="label_large">{realm.name}</Text>
              <Row style={{ marginLeft: 'auto' }}>
                <Text type="label_large">{`${count} Documents`}</Text>
                <IconButton 
                  onClick={() => previewClick(realm)} 
                  size="small" 
                  style={{ 
                    color: colors.blue,
                  }} 
                  variant="none" 
                  icon="visibility" 
                />
              </Row>
            </Row>
          )
        })}
      </div>
    )
  }

  const submit = async () => {
    try {
      setState(s => ({ ...s, loading: true }))

      const config = {
        ...get(edition, 'data.config', {}),
        docs: {},
      }

      Object.keys(docs).map(k => {
        const doc = docs[k];
        doc.items.map(item => {
          if (size(item.rooms)) {
            item.rooms.map(r => {
              const key = `docs.${item.id}.realms`;
              set(config, key, [...get(config, key, []), { id: r.id, name: r.name }])
            })
          }
        })
      })

      const data = {
        data: {
          id: editionId,
          type: 'editions',
          attributes: {
            config
          }
        }
      }

      await sendData({
        url: `/editions/${editionId}`,
        method: 'patch',
        data,
      }, { simulate: false })

      props.onClose && props.onClose(true);
    } catch (err) {
      console.warn(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState(s => ({ ...s, loading: false }))
    }
  }

  return (
    <div className={styles.container}>
      <section className={styles.section}>
        <Text type="headline_large" bold>Location Assignments</Text>
        <Text type="label_large"><b>{get(props, 'subtitleAdd', '')}</b>{'Location assignments hide the selected content from all other divisions.'}</Text>
      </section>

      <section className={styles.section}>
        <Text type="headline_large" bold>Division Previews</Text>
        <div className={styles.marginLeft}>
          <Text style={{ marginBottom: 10, marginTop: 5 }} type="label_large" bold>Division Content</Text>
          <Text style={{ marginBottom: 10 }} type="label_large" italic>Below is a list of divisions and a preview of which content will be visible to those divisions.</Text>
          {renderRealmsList()}
        </div>
      </section>

      <section className={styles.section}>
        <Text type="headline_large" bold>Document Location Assignments</Text>
        {edDocs.loading && <CenterLoader />}
        <div className={styles.marginLeft}>
          {docs.map((doc, i) => <Doc key={`${i}`} doc={doc} updateChecks={updateChecks} onCopy={onCopy} isMobileXS={isMobileXS} rooms={realms} {...state} />)}
        </div>
        {/* {checklists.map((c, i) => <Checklist supersets={supersets.data} checklist={c} key={i} updateChecks={updateChecks} onCopy={onCopy} isMobileXS={isMobileXS} rooms={realms} {...state} />)} */}
      </section>

      <Row center style={{ position: 'fixed', bottom: 10, left: 0, right: 0 }}>
        <Button title="Cancel" bgColor={colors.danger} icon="close" onClick={cancel} />
        <Button title="Save" bgColor={colors.blue} loading={state.loading} icon="save" onClick={submit} style={{ marginLeft: 10 }} />
      </Row>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} />

      <Modal {...preview} />
    </div>
  )
}

export const RealmAssignmentsModal = props => {
  const [state, setState] = useState({
    hasChange: false,
  })
  const [conf, setconf] = useState();

  const { modalProps } = props;

  const closeModal = () => {
    modalProps.onClose();
    setState(s => ({ ...s, hasChange: false }));
  }

  const onClose = (saved) => {
    if (state.hasChange && !saved) {
      setconf({
        title: 'Unsaved changes',
        content: 'You have unsaved data, are you sure you want to close?',
        onOk: () => {
          setconf()
          closeModal()
        },
        onCancel: () => {
          setconf()
        }
      })
    } else {
      closeModal();
    }
  }

  return (
    <>
      <FullScreen {...modalProps} onClose={onClose}>
        <RealmAssignments {...props} onChange={() => setState(s => ({ ...s, hasChange: true }))} onClose={onClose} onCancel={onClose} />
      </FullScreen>
      <Confirm {...conf} />
    </>
  )
}